import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../db';
import { useAuth } from '../../../components/AuthContext';

type NotificationType = 'booking' | 'other';

interface NotificationCounts {
  booking: number;
  other: number;
}

interface NotificationTimes {
  booking: string | null;
  other: string | null;
}

interface NotificationData {
  entity_type: string;
  created_at: string;
  status: string;
}

export const useNotificationCounts = () => {
  const { user } = useAuth();
  const [counts, setCounts] = useState<NotificationCounts>({ booking: 0, other: 0 });
  const [latestTimes, setLatestTimes] = useState<NotificationTimes>({ booking: null, other: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchCounts = async () => {
      try {
        setLoading(true);
        
        // Get all unread notifications for the user
        const { data: notifications, error } = await supabase
          .from('notifications')
          .select('entity_type, created_at, status')
          .eq('user_id', user.id)
          .neq('status', 'read') // Count anything that's not explicitly marked as read
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Calculate counts and latest times
        const newCounts: NotificationCounts = { booking: 0, other: 0 };
        const newTimes: NotificationTimes = { booking: null, other: null };

        (notifications as NotificationData[] || []).forEach(notification => {
          // Count all notifications that aren't read
          const type: NotificationType = 
            notification.entity_type === 'booking'
              ? notification.entity_type as NotificationType
              : 'other';

          // Update counts
          newCounts[type]++;

          // Update latest time if this notification is newer
          if (!newTimes[type] || new Date(notification.created_at) > new Date(newTimes[type]!)) {
            newTimes[type] = notification.created_at;
          }
        });

        setCounts(newCounts);
        setLatestTimes(newTimes);
      } catch (error) {
        console.error('Error fetching notification counts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();

    // Instant update when notifications are marked as read anywhere in the app
    const handleNotifEvent = () => fetchCounts();
    window.addEventListener('notifications-changed', handleNotifEvent);

    // Re-fetch when user tabs back to the app
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchCounts();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Subscribe to changes
    const subscription = supabase
      .channel('notification_counts')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        // New notification added
        const notification = payload.new as NotificationData;
        if (notification.status !== 'read') {
          setCounts(prev => {
            const type: NotificationType = 
              notification.entity_type === 'booking'
                ? notification.entity_type as NotificationType
                : 'other';
            const newCounts = {
              ...prev,
              [type]: prev[type] + 1
            };
            return newCounts;
          });
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        // Status change (e.g., marking as read)
        const oldNotification = payload.old as NotificationData;
        const newNotification = payload.new as NotificationData;
        
        if (oldNotification.status !== 'read' && newNotification.status === 'read') {
          setCounts(prev => {
            const type: NotificationType = 
              newNotification.entity_type === 'booking'
                ? newNotification.entity_type as NotificationType
                : 'other';
            const newCounts = {
              ...prev,
              [type]: Math.max(0, prev[type] - 1)
            };
            return newCounts;
          });
        } else if (oldNotification.status === 'read' && newNotification.status !== 'read') {
          // Handle case where a notification is marked as unread
          setCounts(prev => {
            const type: NotificationType = 
              newNotification.entity_type === 'booking'
                ? newNotification.entity_type as NotificationType
                : 'other';
            const newCounts = {
              ...prev,
              [type]: prev[type] + 1
            };
            return newCounts;
          });
        }
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        // Notification deleted
        const oldNotification = payload.old as NotificationData;
        if (oldNotification.status !== 'read') {
          setCounts(prev => {
            const type: NotificationType = 
              oldNotification.entity_type === 'booking'
                ? oldNotification.entity_type as NotificationType
                : 'other';
            const newCounts = {
              ...prev,
              [type]: Math.max(0, prev[type] - 1)
            };
            return newCounts;
          });
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('notifications-changed', handleNotifEvent);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  return { counts, latestTimes, loading };
}; 